import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../api";
import ImageUpload from "../../components/admin/ImageUpload";
import { IMAGE_SPECS } from "../../constants/imageSpecs";
import RichTextEditor from "../../components/admin/RichTextEditor";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../hooks/use-toast";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

const EMPTY = {
  title: "", slug: "", excerpt: "", body: "", cover: "", category: "General",
  author: "Novocreste Team", published: false, published_at: "", seo_title: "", seo_description: "",
};

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[#0f1e33]">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminArticleEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    adminApi.get("/admin/articles").then((r) => setCategories([...new Set(r.data.map((a) => a.category))])).catch(() => {});
    if (isNew) return;
    adminApi.get(`/admin/articles/${id}`)
      .then((r) => setForm({ ...EMPTY, ...r.data }))
      .catch(() => { toast({ title: "Blog post not found", variant: "destructive" }); navigate("/admin/articles"); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, slug: form.slug || null, excerpt: form.excerpt, body: form.body, cover: form.cover,
        category: form.category || "General", author: form.author, published: form.published,
        published_at: form.published_at || null, seo_title: form.seo_title, seo_description: form.seo_description,
      };
      if (isNew) await adminApi.post("/admin/articles", payload);
      else await adminApi.put(`/admin/articles/${id}`, payload);
      toast({ title: form.published ? "Blog post published" : "Draft saved" });
      navigate("/admin/articles");
    } catch {
      toast({ title: "Save failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  // <input type="date"> wants YYYY-MM-DD
  const dateValue = form.published_at ? form.published_at.slice(0, 10) : "";

  return (
    <div className="max-w-4xl">
      <Link to="/admin/articles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0e7490]"><ArrowLeft className="h-4 w-4" /> All blog posts</Link>
      <div className="mt-3 flex items-center justify-between gap-4">
        <h1 className="font-display text-[28px] text-[#0f1e33]">{isNew ? "New blog post" : "Edit blog post"}</h1>
        <Button onClick={save} disabled={saving} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-11">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {form.published ? "Save & publish" : "Save draft"}
        </Button>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-white p-5 sm:p-7 space-y-5">
        <Field label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Blog post title" /></Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category" hint="Pick an existing one or type a new one.">
            <Input list="article-categories" value={form.category} onChange={(e) => set("category", e.target.value)} />
            <datalist id="article-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Author"><Input value={form.author} onChange={(e) => set("author", e.target.value)} /></Field>
        </div>

        <Field label="Short summary" hint="Shown on blog cards and in search results (1–2 sentences)."><Textarea rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>

        <ImageUpload label="Cover image" value={form.cover} onChange={(url) => set("cover", url)} hint={IMAGE_SPECS.blogCover.hint} minWidth={IMAGE_SPECS.blogCover.minW} minHeight={IMAGE_SPECS.blogCover.minH} />

        <Field label="Blog post body"><RichTextEditor value={form.body} onChange={(html) => set("body", html)} /></Field>

        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <Field label="Publish date"><Input type="date" value={dateValue} onChange={(e) => set("published_at", e.target.value ? `${e.target.value}T09:00:00+00:00` : "")} /></Field>
          <div className="flex items-center gap-3 h-10">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} id="published" />
            <Label htmlFor="published" className="text-[#0f1e33]">{form.published ? "Published (visible on website)" : "Draft (hidden)"}</Label>
          </div>
        </div>

        <details className="rounded-xl border border-border p-4">
          <summary className="cursor-pointer text-sm font-medium text-[#0f1e33]">Search engine settings (optional)</summary>
          <div className="mt-4 space-y-4">
            <Field label="Page title" hint="Leave empty to use the post title."><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
            <Field label="Page description" hint="Leave empty to use the short summary."><Textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></Field>
            <Field label="URL ending" hint="Auto-generated from the title. Only change if needed."><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto" /></Field>
          </div>
        </details>
      </div>
    </div>
  );
}
